import { api } from "@/utils/api";
import env from "@/utils/loadEnv";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from "@capacitor/core";
import { Device } from '@capacitor/device';
import {
  IonButton,
  IonContent,
  IonFooter,
  IonSpinner,
  IonToolbar
} from "@ionic/react";
import { getMessaging, getToken } from "firebase/messaging";
import { motion } from "framer-motion";
import { Bell } from "lucide-react"; // Using Lucide for the Bell icon
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

const NotificationsRequest: React.FC<{ nextStep?: string | undefined, onConfirm?: () => void, onCancel?: () => void }> = ({ nextStep, onConfirm, onCancel }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const requestNotifications = async () => {
    setIsLoading(true);
    try {
      const { user } = await FirebaseAuthentication.getCurrentUser();
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("FirebaseMessaging")) {
        const permission = await FirebaseMessaging.requestPermissions();
        if (permission.receive !== "granted") {
          throw new Error("Notifications permission denied");
        }

        // Get device info
        const deviceId = await Device.getId();
        const deviceInfo = await Device.getInfo();

        // Get FCM token
        const { token } = await FirebaseMessaging.getToken();

        // Save token with device info
        const response = await api.post('/notifications/register-device', {
          userId: user?.uid,
          token,
          deviceId: deviceId.identifier,
          platform: deviceInfo.platform,
          deviceName: deviceInfo.model,
        });

        if (!response.data) {
          throw new Error("Failed to register device");
        }

        // Subscribe to general topic for backward compatibility
        await FirebaseMessaging.subscribeToTopic({ topic: "general" });

        if (onConfirm) {
          onConfirm();
        }

        if (nextStep) {
          history.push(nextStep);
        }

      } else {
        // Web platform logic
        if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
          alert(t("notifications_error", "Service Worker not supported!"));
          return;
        }

        // Check notification permissions
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          alert(t("notifications_denied", "Notifications permission denied!"));
          return;
        }

        // Register service worker
        const registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js"
        );

        // Get FCM token using Firebase Messaging 
        const messaging = getMessaging();
        console.log(messaging)
        const webToken = await getToken(messaging, {
          vapidKey: env.VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (webToken && user?.uid) {
          // Get browser info
          const userAgent = navigator.userAgent;
          const browserInfo = {
            platform: 'web',
            browser: userAgent.includes('Firefox') ? 'Firefox' :
              userAgent.includes('Chrome') ? 'Chrome' :
                userAgent.includes('Safari') ? 'Safari' : 'Unknown'
          };

          // Register device
          const response = await api.post('/notifications/register-device', {
            userId: user?.uid,
            token: webToken,
            deviceId: browserInfo.browser + '-' + Date.now(),
            platform: browserInfo.platform,
            deviceName: browserInfo.browser,
          });

          if (!response.data) {
            throw new Error("Failed to register device");
          }

          alert(t("notifications_enabled", "Notifications enabled successfully!"));

          if (onConfirm) {
            onConfirm();
          }

          if (nextStep) {
            history.push(nextStep);
          }

        } else {
          throw new Error("Failed to get FCM token for web.");
        }
      }
    } catch (err) {
      console.error("Error enabling notifications:", err);
      alert(t("notifications_error", "Error enabling notifications."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <IonContent scrollY={false}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center justify-center h-full text-center px-4 bg-background"
        >
          {/* Icon */}
          <div className="mb-6">
            <div className="flex items-center justify-center w-24 h-24 rounded-full bg-primary/10">
              <Bell className="text-primary w-12 h-12" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-semibold mb-2">
            {t("turn_notifications_on", "Turn on notifications")}
          </h1>

          {/* Subtitle */}
          <p className="text-gray-500 text-sm mb-8">
            {t(
              "notifications_description",
              "Find out straight away when people follow you or like and comment on your posts."
            )}
          </p>

          {/* Buttons */}
          <IonButton
            shape="round"
            expand="block"
            className="mt-8 hidden md:block"
            onClick={requestNotifications}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <IonSpinner name="dots" className="mr-2" />
                {t("enabling_notifications", "Enabling...")}
              </>
            ) : (
              t("turn_notifications_on")
            )}
          </IonButton>
          <IonButton
            fill="clear"
            expand="block"
            color="medium"
            className="text-foreground hidden md:block"
            onClick={() => nextStep && history.push(nextStep)}
            disabled={isLoading}
          >
            {t("maybe_later")}
          </IonButton>
        </motion.div>
      </IonContent>

      {/* Footer for mobile */}
      <IonFooter className="ion-no-border md:hidden">
        <IonToolbar>
          <IonButton
            shape="round"
            expand="block"
            className="mx-4"
            onClick={requestNotifications}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <IonSpinner name="dots" className="mr-2" />
              </>
            ) : (
              t("turn_notifications_on")
            )}
          </IonButton>
          <IonButton
            fill="clear"
            expand="block"
            color="medium"
            className="text-foreground"
            onClick={() => { if (onCancel) onCancel(); if (nextStep) history.push(nextStep); }}
            disabled={isLoading}
          >
            {t("maybe_later")}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </>
  );
};

export default NotificationsRequest;